import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import axios, { type AxiosRequestConfig } from "axios";
import { FireBaseService } from "../firebase/firebase";
import {
  DESIGNER_LIVE_CLIENT_ID,
  DESIGNER_LIVE_CLIENT_ID_HEADER
} from "../services/designer-live/designer-live-client-id";
import LocalStorageService from "./local-storage-service";
import { SESSION_EXPIRED_SIGN_IN_URL } from "./sign-in-reason";
import { clearSessionAndRedirectToLogin, isRefreshInFlight, refreshAccessToken } from "./token-refresh";

declare let window: Window & { skipAlert: boolean; kuika: any; userInfo: any };

const backendUrl = CommonHelperFunctions.GetBackendUrl();

// LocalstorageService
const localStorageService = LocalStorageService.getService();

interface AuthRetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const isRefreshTokenRequest = (config: AuthRetryConfig | undefined): boolean => {
  return config?.url?.includes("refreshtoken") === true;
};

const redirectToLogin = (error: unknown): Promise<never> => {
  clearSessionAndRedirectToLogin();
  return Promise.reject(error);
};

// Add a request interceptor
axios.interceptors.request.use(
  (config) => {
    const urlItems = document.location.pathname.substring(1).split(/\//);
    const token = localStorageService.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.domainName = urlItems[0];

      if (
        urlItems[0].toLowerCase() == "signup" ||
        urlItems[0].toLowerCase() == "login" ||
        urlItems[0].toLowerCase() == "confirm-email" ||
        urlItems[0].toLowerCase() == undefined
      ) {
        config.headers.domainName = "";
      }

      if (window?.kuika?.activeProjectId) {
        config.headers.currentProjectId = window.kuika.activeProjectId;
      }
    }

    if (window?.userInfo?.id) {
      config.headers["X-ClientId"] = window.userInfo.id;
    } else {
      config.headers["X-ClientId"] = CommonHelperFunctions.getClientId();
    }
    // Designer-live origin suppression is per-TAB (X-ClientId is the USER id — using it
    // would hide a user's changes from their own other tabs).
    config.headers[DESIGNER_LIVE_CLIENT_ID_HEADER] = DESIGNER_LIVE_CLIENT_ID;

    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Add a response interceptor
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest: AuthRetryConfig | undefined = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status: number = error.response.status;

    if (isRefreshTokenRequest(originalRequest)) {
      // While a refresh is in flight its own rejection is the caller's to handle —
      // redirecting here would race the caller's handling of the same failure.
      if (!isRefreshInFlight()) {
        clearSessionAndRedirectToLogin();
      }
      return Promise.reject(error);
    }

    if (status === 401 && originalRequest.url === `${backendUrl}/auth/login`) {
      const firebase = new FireBaseService();
      firebase.logEvent("network_error", {
        error
      });
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Siblings that 401 together all await the same refresh — see token-refresh.ts
      // for why a second concurrent POST would retire the token under the first one.
      try {
        const accessToken: string = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch (err) {
        // The session cannot be renewed (no local refresh token, non-200, network
        // error). Every awaiting sibling lands here too, so none of them hang.
        return redirectToLogin(err);
      }
    }

    if (status === 400 && error.config.url.indexOf("refreshtoken") > -1) {
      localStorageService.clearToken();
      window.skipAlert = true;
      window.location.href = SESSION_EXPIRED_SIGN_IN_URL;
    }

    return Promise.reject(error);
  }
);
